import * as Schema from "effect/Schema";
import {
  DESIGN_DELETE_REPORTED_ACTIVITY_KIND,
  deriveThreadExecutionPhase,
  readDesignDeleteReport,
} from "@afar-research/afar";
export {
  deriveAgentTaskListState,
  deriveAgentTaskListStates,
  type AgentTaskListState,
} from "@afar-research/client-runtime/state/agent-task-list";
import {
  classifyThreadAttentionActivity,
  type ThreadOperationIdentity,
  type ThreadOperationSummaryInput,
} from "@afar-research/afar";
import {
  parseCodexCollabToolItem,
  parseCodexSubagentActivityItem,
} from "@afar-research/client-runtime/state/subagent-runtime";
import { compareOrchestrationActivitiesByOrder as compareActivitiesByOrder } from "@afar-research/client-runtime/state/orchestration-activity-order";
import type { WorkflowToolCard } from "./components/workflow/workflowCards.logic";
import {
  PROVIDER_INTERRUPT_FALLBACK_ACTIVITY_KIND,
  PROVIDER_INTERRUPT_FALLBACK_DETAIL,
  PROVIDER_INTERRUPT_FALLBACK_SUMMARY,
} from "@afar-research/shared/providerInterruptFallback";
import {
  ApprovalRequestId,
  type ProviderApprovalDecision,
  type ProviderApprovalOption,
  ToolActivityIcon,
  ToolActivitySource,
  type EventId,
  isToolLifecycleItemType,
  type OrchestrationLatestTurn,
  type OrchestrationThreadActivity,
  type OrchestrationProposedPlanId,
  ProviderDriverKind,
  type ToolLifecycleItemType,
  type UserInputQuestion,
  type ThreadId,
  type TurnId,
  type DesignDeleteReport,
} from "@afar-research/contracts";

import type {
  ChatMessage,
  ProposedPlan,
  SessionPhase,
  Thread,
  ThreadSession,
  TurnDiffSummary,
} from "./types";
import { formatDuration } from "./durationFormat";

export { formatDuration } from "./durationFormat";

export type ProviderPickerKind = ProviderDriverKind;

export const PROVIDER_OPTIONS: Array<{
  value: ProviderPickerKind;
  label: string;
  available: boolean;
  /** Shown on the model picker sidebar when relevant */
  pickerSidebarBadge?: "new" | "soon";
}> = [
  { value: ProviderDriverKind.make("codex"), label: "Codex", available: true },
  {
    value: ProviderDriverKind.make("claudeAgent"),
    label: "Claude Code",
    available: true,
    pickerSidebarBadge: "new",
  },
];

export type WorkLogToolLifecycleStatus =
  | "inProgress"
  | "completed"
  | "failed"
  | "declined"
  | "stopped";

export interface WorkLogEntry {
  id: string;
  createdAt: string;
  sequence?: number;
  turnId?: TurnId | null;
  label: string;
  detail?: string;
  command?: string;
  rawCommand?: string;
  changedFiles?: ReadonlyArray<string>;
  tone: "thinking" | "tool" | "info" | "error";
  toolTitle?: string;
  toolData?: unknown;
  itemType?: ToolLifecycleItemType;
  requestKind?: PendingApproval["requestKind"];
  /** Website, native app or connector logo this activity acted through. */
  toolIcon?: ToolActivityIcon;
  /** The named product behind the tool, when it is not the agent itself. */
  toolSource?: ToolActivitySource;
  /** From runtime item / task payload `status` when present (e.g. tool.updated). */
  toolLifecycleStatus?: WorkLogToolLifecycleStatus;
  /** Originating orchestration activity kind (e.g. `user-input.requested`) for row chrome. */
  sourceActivityKind?: OrchestrationThreadActivity["kind"];
  /** Native Codex spawn batch represented by this single timeline call to action. */
  subagentSpawnBatch?: {
    readonly id: string;
    readonly agentCount: number;
    readonly agentIds: ReadonlyArray<string>;
  };
}

interface DerivedWorkLogEntry extends WorkLogEntry {
  activityKind: OrchestrationThreadActivity["kind"];
  collapseKey?: string;
  toolCallId?: string;
}

export interface PendingApproval {
  requestId: ApprovalRequestId;
  turnId: TurnId | null;
  requestKind: "command" | "file-read" | "file-change" | "mcp-elicitation";
  createdAt: string;
  detail?: string;
  /** The connector or app an MCP elicitation names, when it names one. */
  appName?: string;
  /** Provider-advertised choices; empty means the composer's standard pair. */
  options: ReadonlyArray<ProviderApprovalOption>;
}

export interface PendingUserInput {
  requestId: ApprovalRequestId;
  revision: EventId;
  turnId: TurnId | null;
  createdAt: string;
  questions: ReadonlyArray<UserInputQuestion>;
}

export interface LatestProposedPlanState {
  id: OrchestrationProposedPlanId;
  createdAt: string;
  updatedAt: string;
  turnId: TurnId | null;
  planMarkdown: string;
  implementedAt: string | null;
  implementationThreadId: ThreadId | null;
}

export type TimelineEntry =
  | {
      id: string;
      kind: "message";
      createdAt: string;
      sequence?: number;
      effectiveTurnId?: TurnId;
      isSteeredPrompt?: true;
      /** Set when this user message submitted a Workspace Action. */
      operation?: ThreadOperationSummaryInput;
      message: ChatMessage;
    }
  | {
      id: string;
      kind: "proposed-plan";
      createdAt: string;
      sequence?: number;
      proposedPlan: ProposedPlan;
    }
  | {
      id: string;
      kind: "work";
      createdAt: string;
      sequence?: number;
      entry: WorkLogEntry;
    }
  | {
      id: string;
      kind: "design-delete-report";
      createdAt: string;
      sequence?: number;
      report: DesignDeleteReport;
    }
  | {
      id: string;
      kind: "workflow-card";
      createdAt: string;
      sequence?: number;
      /** An agent's App Control call that presented, ran or read a Workflow (ADR 0141). */
      card: WorkflowToolCard;
    };

export function workLogEntryIsToolLike(entry: WorkLogEntry): boolean {
  if (entry.tone === "tool" || entry.tone === "thinking" || entry.tone === "error") {
    return true;
  }
  if (entry.command !== undefined && entry.command.trim().length > 0) {
    return true;
  }
  if (entry.requestKind !== undefined) {
    return true;
  }
  return entry.itemType !== undefined && isToolLifecycleItemType(entry.itemType);
}

/** Heuristic: providers often emit successful lifecycle status while error text lives in `detail` / `command`. */
function toolDetailTextLooksLikeFailure(text: string): boolean {
  const t = text.toLowerCase();
  if (t.includes("file not found")) {
    return true;
  }
  if (t.includes("no files found")) {
    return true;
  }
  if (
    t.includes("enoent") ||
    t.includes("no such file or directory") ||
    t.includes("no such file")
  ) {
    return true;
  }
  if (t.includes("cannot find path") && t.includes("because it does not exist")) {
    return true;
  }
  if (t.includes("commandnotfoundexception")) {
    return true;
  }
  if (t.includes("is not recognized as the name of a cmdlet")) {
    return true;
  }
  if (t.includes("is not recognized") && t.includes("the term '")) {
    return true;
  }
  if (t.includes("a parameter cannot be found that matches parameter name")) {
    return true;
  }
  if (t.includes("command not found")) {
    return true;
  }
  if (/<exited with exit code\s+[1-9]\d*\s*>/i.test(text)) {
    return true;
  }
  if (/exit(?:ed)? with exit code\s+[1-9]\d*/i.test(text)) {
    return true;
  }
  if (/exit code\s*[:\s]\s*[1-9]\d*\b/i.test(text)) {
    return true;
  }
  return false;
}

/** True when the row should show a failure affordance (explicit status/tone or error-shaped tool output). */
export function workEntryIndicatesToolFailure(entry: WorkLogEntry): boolean {
  if (entry.tone === "error") {
    return true;
  }
  const ls = entry.toolLifecycleStatus;
  if (ls === "failed" || ls === "declined") {
    return true;
  }
  if (!workLogEntryIsToolLike(entry)) {
    return false;
  }
  const parts: string[] = [];
  if (entry.detail) {
    parts.push(entry.detail);
  }
  if (entry.command) {
    parts.push(entry.command);
  }
  const blob = parts.join("\n");
  if (blob.length === 0) {
    return false;
  }
  return toolDetailTextLooksLikeFailure(blob);
}

/** Tool/command row completed without failure (blue check affordance). */
export function workEntryIndicatesToolSuccess(entry: WorkLogEntry): boolean {
  if (!workLogEntryIsToolLike(entry)) {
    return false;
  }
  if (workEntryIndicatesToolFailure(entry)) {
    return false;
  }
  if (entry.tone === "thinking") {
    return false;
  }
  const ls = entry.toolLifecycleStatus;
  if (ls === "failed" || ls === "declined") {
    return false;
  }
  if (ls === "inProgress") {
    return false;
  }
  if (ls === "stopped") {
    return false;
  }
  return true;
}

/** Tool-like row with neither clear success nor failure (empty, incomplete, in progress, etc.). */
export function workEntryIndicatesToolNeutralStatus(entry: WorkLogEntry): boolean {
  if (!workLogEntryIsToolLike(entry)) {
    return false;
  }
  if (workEntryIndicatesToolFailure(entry)) {
    return false;
  }
  if (workEntryIndicatesToolSuccess(entry)) {
    return false;
  }
  return true;
}

export function formatElapsed(startIso: string, endIso: string | undefined): string | null {
  if (!endIso) return null;
  const startedAt = Date.parse(startIso);
  const endedAt = Date.parse(endIso);
  if (Number.isNaN(startedAt) || Number.isNaN(endedAt) || endedAt < startedAt) {
    return null;
  }
  return formatDuration(endedAt - startedAt);
}

type LatestTurnTiming = Pick<OrchestrationLatestTurn, "turnId" | "startedAt" | "completedAt">;
type SessionActivityState = Pick<NonNullable<Thread["session"]>, "status" | "activeTurnId">;

export function isLatestTurnSettled(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
): boolean {
  if (!latestTurn?.startedAt) return false;
  if (!latestTurn.completedAt) return false;
  if (!session) return true;
  if (session.status === "running") return false;
  return true;
}

export function deriveActiveWorkStartedAt(
  latestTurn: LatestTurnTiming | null,
  session: SessionActivityState | null,
  sendStartedAt: string | null,
): string | null {
  const runningTurnId = session?.status === "running" ? session.activeTurnId : null;
  if (runningTurnId !== null) {
    if (latestTurn?.turnId === runningTurnId) {
      return latestTurn.startedAt ?? sendStartedAt;
    }
    return sendStartedAt;
  }
  if (!isLatestTurnSettled(latestTurn, session)) {
    return latestTurn?.startedAt ?? sendStartedAt;
  }
  return sendStartedAt;
}

function requestKindFromRequestType(requestType: unknown): PendingApproval["requestKind"] | null {
  switch (requestType) {
    case "command_execution_approval":
    case "exec_command_approval":
    case "dynamic_tool_call":
      return "command";
    case "file_read_approval":
      return "file-read";
    case "file_change_approval":
    case "apply_patch_approval":
      return "file-change";
    case "mcp_elicitation_approval":
      return "mcp-elicitation";
    default:
      return null;
  }
}

const isApprovalDecision = (value: unknown): value is ProviderApprovalDecision =>
  value === "accept" ||
  value === "acceptForSession" ||
  value === "acceptAlways" ||
  value === "decline" ||
  value === "cancel";

/** An option the composer cannot act on is worse than none, so drop malformed entries. */
function normalizeApprovalOptions(value: unknown): ReadonlyArray<ProviderApprovalOption> {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((candidate) => {
    if (typeof candidate !== "object" || candidate === null) return [];
    const option = candidate as Record<string, unknown>;
    const label = typeof option.label === "string" ? option.label.trim() : "";
    if (label.length === 0 || !isApprovalDecision(option.decision) || seen.has(option.decision)) {
      return [];
    }
    seen.add(option.decision);
    const warning = typeof option.warning === "string" ? option.warning.trim() : "";
    return [{ decision: option.decision, label, ...(warning ? { warning } : {}) }];
  });
}

function isStalePendingRequestFailureDetail(detail: string | undefined): boolean {
  const normalized = detail?.toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized.includes("stale pending approval request") ||
    normalized.includes("stale pending user-input request") ||
    normalized.includes("unknown pending approval request") ||
    normalized.includes("unknown pending permission request") ||
    normalized.includes("unknown pending user-input request") ||
    normalized.includes("unknown pending user input request") ||
    normalized.includes("unknown pending codex user input request")
  );
}

export function derivePendingApprovals(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingApproval[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingApproval>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const transition = classifyThreadAttentionActivity(activity);
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.make(payload.requestId)
        : null;
    const requestKind =
      payload &&
      (payload.requestKind === "command" ||
        payload.requestKind === "file-read" ||
        payload.requestKind === "file-change" ||
        payload.requestKind === "mcp-elicitation")
        ? payload.requestKind
        : payload
          ? requestKindFromRequestType(payload.requestType)
          : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;
    const appName =
      payload && typeof payload.appName === "string" && payload.appName.trim().length > 0
        ? payload.appName
        : undefined;
    const options = normalizeApprovalOptions(payload?.options);

    if (
      transition?.type === "approval" &&
      transition.outcome === "requested" &&
      requestId &&
      requestKind
    ) {
      openByRequestId.set(requestId, {
        requestId,
        turnId: activity.turnId,
        requestKind,
        createdAt: activity.createdAt,
        ...(detail ? { detail } : {}),
        ...(appName ? { appName } : {}),
        options,
      });
      continue;
    }

    if (transition?.type === "approval" && transition.outcome !== "requested" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.approval.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
      continue;
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

function parseUserInputQuestions(
  payload: Record<string, unknown> | null,
): ReadonlyArray<UserInputQuestion> | null {
  const questions = payload?.questions;
  if (!Array.isArray(questions)) {
    return null;
  }
  const parsed = questions
    .map<UserInputQuestion | null>((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const question = entry as Record<string, unknown>;
      if (
        typeof question.id !== "string" ||
        typeof question.header !== "string" ||
        typeof question.question !== "string" ||
        !Array.isArray(question.options)
      ) {
        return null;
      }
      const options = question.options
        .map<UserInputQuestion["options"][number] | null>((option) => {
          if (!option || typeof option !== "object") return null;
          const optionRecord = option as Record<string, unknown>;
          if (
            typeof optionRecord.label !== "string" ||
            typeof optionRecord.description !== "string"
          ) {
            return null;
          }
          return {
            label: optionRecord.label,
            description: optionRecord.description,
          };
        })
        .filter((option): option is UserInputQuestion["options"][number] => option !== null);
      if (options.length === 0) {
        return null;
      }
      return {
        id: question.id,
        header: question.header,
        question: question.question,
        options,
        multiSelect: question.multiSelect === true,
      };
    })
    .filter((question): question is UserInputQuestion => question !== null);
  return parsed.length > 0 ? parsed : null;
}

export function derivePendingUserInputs(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): PendingUserInput[] {
  const openByRequestId = new Map<ApprovalRequestId, PendingUserInput>();
  const ordered = [...activities].toSorted(compareActivitiesByOrder);

  for (const activity of ordered) {
    const transition = classifyThreadAttentionActivity(activity);
    const payload =
      activity.payload && typeof activity.payload === "object"
        ? (activity.payload as Record<string, unknown>)
        : null;
    const requestId =
      payload && typeof payload.requestId === "string"
        ? ApprovalRequestId.make(payload.requestId)
        : null;
    const detail = payload && typeof payload.detail === "string" ? payload.detail : undefined;

    if (transition?.type === "user-input" && transition.outcome === "requested" && requestId) {
      const questions = parseUserInputQuestions(payload);
      if (!questions) {
        continue;
      }
      openByRequestId.set(requestId, {
        requestId,
        revision: activity.id,
        turnId: activity.turnId,
        createdAt: activity.createdAt,
        questions,
      });
      continue;
    }

    if (transition?.type === "user-input" && transition.outcome !== "requested" && requestId) {
      openByRequestId.delete(requestId);
      continue;
    }

    if (
      activity.kind === "provider.user-input.respond.failed" &&
      requestId &&
      isStalePendingRequestFailureDetail(detail)
    ) {
      openByRequestId.delete(requestId);
    }
  }

  return [...openByRequestId.values()].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

export function findLatestProposedPlan(
  proposedPlans: ReadonlyArray<ProposedPlan>,
  latestTurnId: TurnId | string | null | undefined,
): LatestProposedPlanState | null {
  if (latestTurnId) {
    const matchingTurnPlan = [...proposedPlans]
      .filter((proposedPlan) => proposedPlan.turnId === latestTurnId)
      .toSorted(
        (left, right) =>
          left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
      )
      .at(-1);
    if (matchingTurnPlan) {
      return toLatestProposedPlanState(matchingTurnPlan);
    }
  }

  const latestPlan = [...proposedPlans]
    .toSorted(
      (left, right) =>
        left.updatedAt.localeCompare(right.updatedAt) || left.id.localeCompare(right.id),
    )
    .at(-1);
  if (!latestPlan) {
    return null;
  }

  return toLatestProposedPlanState(latestPlan);
}

export function hasActionableProposedPlan(
  proposedPlan: LatestProposedPlanState | Pick<ProposedPlan, "implementedAt"> | null,
): boolean {
  return proposedPlan !== null && proposedPlan.implementedAt === null;
}

export function deriveWorkLogEntries(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): WorkLogEntry[] {
  const ordered = [...activities].toSorted(compareActivitiesByOrder);
  const entries: DerivedWorkLogEntry[] = [];
  for (const activity of ordered) {
    if (isSubagentOnlyWorkLogActivity(activity)) continue;
    if (activity.kind === "tool.started" && !extractSubagentSpawnBatch(activity.payload)) continue;
    if (activity.kind === "task.started") continue;
    if (activity.kind === "context-window.updated") continue;
    // Native compaction evidence drives recovery, but successful boundaries are not conversation content.
    if (activity.kind === "context-compaction" && activity.tone !== "error") continue;
    if (isThreadQueueWorkLogActivity(activity.kind)) continue;
    // The Delete Design Operation's report is its own timeline bubble — the one
    // and only account of that act — not a line in the Work Log beside it.
    if (activity.kind === DESIGN_DELETE_REPORTED_ACTIVITY_KIND) continue;
    if (activity.summary === "Checkpoint captured") continue;
    // Rows of the retired Contribution History still sit in older logs.
    if (activity.kind === "git.commit.created") continue;
    if (isPlanBoundaryToolActivity(activity)) continue;
    entries.push(toDerivedWorkLogEntry(activity));
  }
  return collapseDerivedWorkLogEntries(entries).map((entry) => {
    const { activityKind, collapseKey: _collapseKey, ...rest } = entry;
    return Object.assign(rest, { sourceActivityKind: activityKind });
  });
}

function isSubagentOnlyWorkLogActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind === "subagent.lifecycle" || activity.kind === "subagent.message.completed") {
    return true;
  }
  const payload = asRecord(activity.payload);
  // The provider said this row belongs to an agent roster rather than the
  // conversation. A workflow repeats every member on every tick, so rendering
  // them here buries the turn the user is actually reading.
  if (payload?.timelineBypass === true) return true;
  return asTrimmedString(payload?.childThreadId) !== null;
}

function isThreadQueueWorkLogActivity(kind: string): boolean {
  // Thread Queue lifecycle is owned by the pending stack / timeline — not Work Log.
  // Setup ceremony (queue/baseline/context bind) stays out; terminal outcomes remain.
  return (
    kind.startsWith("thread.queue.entry.") ||
    // Matched by shape rather than by scope, so a retired scope's persisted
    // rows stay out of Work Log without naming the scope here.
    kind.endsWith(".operation.queued") ||
    kind.endsWith(".operation.baselined") ||
    kind.endsWith(".operation.preflighted") ||
    kind === "schedule.run.created" ||
    kind.endsWith(".context-bound") ||
    kind.endsWith(".context-adopted") ||
    kind.includes("preflight")
  );
}

function isPlanBoundaryToolActivity(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "tool.updated" && activity.kind !== "tool.completed") {
    return false;
  }

  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  return typeof payload?.detail === "string" && payload.detail.startsWith("ExitPlanMode:");
}

function extractWorkLogToolLifecycleStatus(
  payload: Record<string, unknown> | null,
): WorkLogToolLifecycleStatus | undefined {
  if (!payload) {
    return undefined;
  }
  const s = payload.status;
  if (
    s === "inProgress" ||
    s === "completed" ||
    s === "failed" ||
    s === "declined" ||
    s === "stopped"
  ) {
    return s;
  }
  return undefined;
}

const isToolActivityIcon = Schema.is(ToolActivityIcon);
const isToolActivitySource = Schema.is(ToolActivitySource);

/**
 * Tool presentation reaches the client as opaque activity payload. It is
 * decoded against the contract rather than cast: an icon URL from a connector
 * ends up in an `<img src>`, so a shape that does not match is dropped.
 */
function decodeToolActivityIcon(value: unknown): ToolActivityIcon | undefined {
  return isToolActivityIcon(value) ? value : undefined;
}

function decodeToolActivitySource(value: unknown): ToolActivitySource | undefined {
  return isToolActivitySource(value) ? value : undefined;
}

function toDerivedWorkLogEntry(activity: OrchestrationThreadActivity): DerivedWorkLogEntry {
  const isLegacyProviderInterruptFailure = activity.kind === "provider.turn.interrupt.failed";
  const payload =
    activity.payload && typeof activity.payload === "object"
      ? (activity.payload as Record<string, unknown>)
      : null;
  const commandPreview = extractToolCommand(payload);
  const changedFiles = extractChangedFiles(payload);
  const title = extractToolTitle(payload);
  const isTaskActivity = activity.kind === "task.progress" || activity.kind === "task.completed";
  const taskSummary =
    isTaskActivity && typeof payload?.summary === "string" && payload.summary.length > 0
      ? payload.summary
      : null;
  const taskDetailAsLabel =
    isTaskActivity &&
    !taskSummary &&
    typeof payload?.detail === "string" &&
    payload.detail.length > 0
      ? payload.detail
      : null;
  const taskLabel = taskSummary || taskDetailAsLabel;
  const conversationCommand =
    activity.kind === "context-compaction.requested"
      ? "/compact"
      : activity.kind === "provider-goal.requested" &&
          payload?.action === "start" &&
          typeof payload.objective === "string"
        ? `/goal ${payload.objective}`
        : null;
  const detail =
    // A compaction's figures are already in the server-written summary.
    isLegacyProviderInterruptFailure
      ? PROVIDER_INTERRUPT_FALLBACK_DETAIL
      : isTaskActivity
        ? !taskDetailAsLabel &&
          payload &&
          typeof payload.detail === "string" &&
          payload.detail.length > 0
          ? stripTrailingExitCode(payload.detail).output
          : null
        : extractToolDetail(payload, title ?? activity.summary);
  const toolCallId = isTaskActivity ? null : extractToolCallId(payload);
  const entry: DerivedWorkLogEntry = {
    id: activity.id,
    createdAt: activity.createdAt,
    ...(activity.sequence !== undefined ? { sequence: activity.sequence } : {}),
    turnId: activity.turnId,
    label: isLegacyProviderInterruptFailure
      ? PROVIDER_INTERRUPT_FALLBACK_SUMMARY
      : conversationCommand || taskLabel || activity.summary,
    tone: isLegacyProviderInterruptFailure
      ? "info"
      : activity.kind === "task.progress"
        ? "thinking"
        : activity.tone === "approval"
          ? "info"
          : activity.tone,
    activityKind: isLegacyProviderInterruptFailure
      ? PROVIDER_INTERRUPT_FALLBACK_ACTIVITY_KIND
      : activity.kind,
  };
  const itemType = extractWorkLogItemType(payload);
  const requestKind = extractWorkLogRequestKind(payload);
  if (detail) {
    entry.detail = detail;
  }
  if (commandPreview.command) {
    entry.command = commandPreview.command;
  }
  if (commandPreview.rawCommand) {
    entry.rawCommand = commandPreview.rawCommand;
  }
  if (changedFiles.length > 0) {
    entry.changedFiles = changedFiles;
  }
  if (title) {
    entry.toolTitle = title;
  }
  if (itemType === "mcp_tool_call" || itemType === "collab_agent_tool_call") {
    const data = asRecord(payload?.data);
    if (data?.item !== undefined) {
      entry.toolData = data.item;
    }
  }
  const subagentSpawnBatch = extractSubagentSpawnBatch(payload);
  if (subagentSpawnBatch) {
    entry.subagentSpawnBatch = subagentSpawnBatch;
  }
  if (itemType) {
    entry.itemType = itemType;
  }
  if (requestKind) {
    entry.requestKind = requestKind;
  }
  const toolIcon = decodeToolActivityIcon(payload?.toolIcon);
  if (toolIcon) {
    entry.toolIcon = toolIcon;
  }
  const toolSource = decodeToolActivitySource(payload?.toolSource);
  if (toolSource) {
    entry.toolSource = toolSource;
  }
  if (toolCallId) {
    entry.toolCallId = toolCallId;
  }
  let toolLifecycleStatus = extractWorkLogToolLifecycleStatus(payload);
  if (!toolLifecycleStatus && activity.kind === "tool.completed") {
    toolLifecycleStatus = "completed";
  }
  if (toolLifecycleStatus) {
    entry.toolLifecycleStatus = toolLifecycleStatus;
  }
  const collapseKey = deriveToolLifecycleCollapseKey(entry);
  if (collapseKey) {
    entry.collapseKey = collapseKey;
  }
  return entry;
}

function collapseDerivedWorkLogEntries(
  entries: ReadonlyArray<DerivedWorkLogEntry>,
): DerivedWorkLogEntry[] {
  const collapsed: DerivedWorkLogEntry[] = [];
  for (const entry of entries) {
    const previous = collapsed.at(-1);
    if (previous && shouldCollapseToolLifecycleEntries(previous, entry)) {
      collapsed[collapsed.length - 1] = mergeDerivedWorkLogEntries(previous, entry);
      continue;
    }
    collapsed.push(entry);
  }
  return collapsed;
}

function shouldCollapseToolLifecycleEntries(
  previous: DerivedWorkLogEntry,
  next: DerivedWorkLogEntry,
): boolean {
  if (
    previous.activityKind !== "tool.started" &&
    previous.activityKind !== "tool.updated" &&
    previous.activityKind !== "tool.completed"
  ) {
    return false;
  }
  if (
    next.activityKind !== "tool.started" &&
    next.activityKind !== "tool.updated" &&
    next.activityKind !== "tool.completed"
  ) {
    return false;
  }
  if (previous.activityKind === "tool.completed") {
    return false;
  }
  if (previous.collapseKey !== undefined && previous.collapseKey === next.collapseKey) {
    return true;
  }
  return (
    previous.toolCallId !== undefined &&
    next.toolCallId === undefined &&
    previous.itemType === next.itemType &&
    normalizeCompactToolLabel(previous.toolTitle ?? previous.label) ===
      normalizeCompactToolLabel(next.toolTitle ?? next.label)
  );
}

function mergeDerivedWorkLogEntries(
  previous: DerivedWorkLogEntry,
  next: DerivedWorkLogEntry,
): DerivedWorkLogEntry {
  const changedFiles = mergeChangedFiles(previous.changedFiles, next.changedFiles);
  const detail = next.detail ?? previous.detail;
  const command = next.command ?? previous.command;
  const rawCommand = next.rawCommand ?? previous.rawCommand;
  const toolTitle = next.toolTitle ?? previous.toolTitle;
  const itemType = next.itemType ?? previous.itemType;
  const requestKind = next.requestKind ?? previous.requestKind;
  const collapseKey = next.collapseKey ?? previous.collapseKey;
  const toolCallId = next.toolCallId ?? previous.toolCallId;
  const toolLifecycleStatus = next.toolLifecycleStatus ?? previous.toolLifecycleStatus;
  const toolData = next.toolData ?? previous.toolData;
  const subagentSpawnBatch = next.subagentSpawnBatch ?? previous.subagentSpawnBatch;
  return {
    ...previous,
    ...next,
    ...(previous.sequence !== undefined ? { sequence: previous.sequence } : {}),
    ...(detail ? { detail } : {}),
    ...(command ? { command } : {}),
    ...(rawCommand ? { rawCommand } : {}),
    ...(changedFiles.length > 0 ? { changedFiles } : {}),
    ...(toolTitle ? { toolTitle } : {}),
    ...(itemType ? { itemType } : {}),
    ...(requestKind ? { requestKind } : {}),
    ...(collapseKey ? { collapseKey } : {}),
    ...(toolCallId ? { toolCallId } : {}),
    ...(toolLifecycleStatus !== undefined ? { toolLifecycleStatus } : {}),
    ...(toolData !== undefined ? { toolData } : {}),
    ...(subagentSpawnBatch ? { subagentSpawnBatch } : {}),
  };
}

function mergeChangedFiles(
  previous: ReadonlyArray<string> | undefined,
  next: ReadonlyArray<string> | undefined,
): string[] {
  const merged = [...(previous ?? []), ...(next ?? [])];
  if (merged.length === 0) {
    return [];
  }
  return [...new Set(merged)];
}

function deriveToolLifecycleCollapseKey(entry: DerivedWorkLogEntry): string | undefined {
  if (
    entry.activityKind !== "tool.started" &&
    entry.activityKind !== "tool.updated" &&
    entry.activityKind !== "tool.completed"
  ) {
    return undefined;
  }
  if (entry.toolCallId) {
    return `tool:${entry.toolCallId}`;
  }
  const normalizedLabel = normalizeCompactToolLabel(entry.toolTitle ?? entry.label);
  const detail = entry.detail?.trim() ?? "";
  const itemType = entry.itemType ?? "";
  if (normalizedLabel.length === 0 && detail.length === 0 && itemType.length === 0) {
    return undefined;
  }
  return [itemType, normalizedLabel, detail].join("\u001f");
}

function normalizeCompactToolLabel(value: string): string {
  return value.replace(/\s+(?:complete|completed)\s*$/i, "").trim();
}

function toLatestProposedPlanState(proposedPlan: ProposedPlan): LatestProposedPlanState {
  return {
    id: proposedPlan.id,
    createdAt: proposedPlan.createdAt,
    updatedAt: proposedPlan.updatedAt,
    turnId: proposedPlan.turnId,
    planMarkdown: proposedPlan.planMarkdown,
    implementedAt: proposedPlan.implementedAt,
    implementationThreadId: proposedPlan.implementationThreadId,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function extractSubagentSpawnBatch(
  payload: unknown,
): WorkLogEntry["subagentSpawnBatch"] | undefined {
  const item = parseCodexCollabToolItem(payload);
  if (item?.tool === "spawnAgent") {
    const agentIds = [...new Set(item.receiverThreadIds)];
    return agentIds.length > 0 ? { id: item.id, agentCount: agentIds.length, agentIds } : undefined;
  }
  const activity = parseCodexSubagentActivityItem(payload);
  return activity?.kind === "started"
    ? { id: activity.id, agentCount: 1, agentIds: [activity.agentThreadId] }
    : undefined;
}

function asTrimmedString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function trimMatchingOuterQuotes(value: string): string {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
    (trimmed.startsWith('"') && trimmed.endsWith('"'))
  ) {
    const unquoted = trimmed.slice(1, -1).trim();
    return unquoted.length > 0 ? unquoted : trimmed;
  }
  return trimmed;
}

function executableBasename(value: string): string | null {
  const trimmed = trimMatchingOuterQuotes(value);
  if (trimmed.length === 0) {
    return null;
  }
  const normalized = trimmed.replace(/\\/g, "/");
  const segments = normalized.split("/");
  const last = segments.at(-1)?.trim() ?? "";
  return last.length > 0 ? last.toLowerCase() : null;
}

function splitExecutableAndRest(value: string): { executable: string; rest: string } | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  if (trimmed.startsWith('"') || trimmed.startsWith("'")) {
    const quote = trimmed.charAt(0);
    const closeIndex = trimmed.indexOf(quote, 1);
    if (closeIndex <= 0) {
      return null;
    }
    return {
      executable: trimmed.slice(0, closeIndex + 1),
      rest: trimmed.slice(closeIndex + 1).trim(),
    };
  }

  const firstWhitespace = trimmed.search(/\s/);
  if (firstWhitespace < 0) {
    return {
      executable: trimmed,
      rest: "",
    };
  }

  return {
    executable: trimmed.slice(0, firstWhitespace),
    rest: trimmed.slice(firstWhitespace).trim(),
  };
}

const SHELL_WRAPPER_SPECS = [
  {
    executables: ["pwsh", "pwsh.exe", "powershell", "powershell.exe"],
    wrapperFlagPattern: /(?:^|\s)-command\s+/i,
  },
  {
    executables: ["cmd", "cmd.exe"],
    wrapperFlagPattern: /(?:^|\s)\/c\s+/i,
  },
  {
    executables: ["bash", "sh", "zsh"],
    wrapperFlagPattern: /(?:^|\s)-(?:l)?c\s+/i,
  },
] as const;

function findShellWrapperSpec(shell: string) {
  return SHELL_WRAPPER_SPECS.find((spec) =>
    (spec.executables as ReadonlyArray<string>).includes(shell),
  );
}

function unwrapCommandRemainder(value: string, wrapperFlagPattern: RegExp): string | null {
  const match = wrapperFlagPattern.exec(value);
  if (!match) {
    return null;
  }

  const command = value.slice(match.index + match[0].length).trim();
  if (command.length === 0) {
    return null;
  }

  const unwrapped = trimMatchingOuterQuotes(command);
  return unwrapped.length > 0 ? unwrapped : null;
}

function unwrapKnownShellCommandWrapper(value: string): string {
  const split = splitExecutableAndRest(value);
  if (!split || split.rest.length === 0) {
    return value;
  }

  const shell = executableBasename(split.executable);
  if (!shell) {
    return value;
  }

  const spec = findShellWrapperSpec(shell);
  if (!spec) {
    return value;
  }

  return unwrapCommandRemainder(split.rest, spec.wrapperFlagPattern) ?? value;
}

function formatCommandArrayPart(value: string): string {
  return /[\s"'`]/.test(value) ? `"${value.replace(/"/g, '\\"')}"` : value;
}

function formatCommandValue(value: unknown): string | null {
  const direct = asTrimmedString(value);
  if (direct) {
    return direct;
  }
  if (!Array.isArray(value)) {
    return null;
  }
  const parts: Array<string> = [];
  for (const entry of value) {
    const part = asTrimmedString(entry);
    if (part !== null) {
      parts.push(part);
    }
  }
  if (parts.length === 0) {
    return null;
  }
  return parts.map((part) => formatCommandArrayPart(part)).join(" ");
}

function normalizeCommandValue(value: unknown): string | null {
  const formatted = formatCommandValue(value);
  return formatted ? unwrapKnownShellCommandWrapper(formatted) : null;
}

function toRawToolCommand(value: unknown, normalizedCommand: string | null): string | null {
  const formatted = formatCommandValue(value);
  if (!formatted || normalizedCommand === null) {
    return null;
  }
  return formatted === normalizedCommand ? null : formatted;
}

function extractToolCommand(payload: Record<string, unknown> | null): {
  command: string | null;
  rawCommand: string | null;
} {
  const data = asRecord(payload?.data);
  const item = asRecord(data?.item);
  const itemResult = asRecord(item?.result);
  const itemInput = asRecord(item?.input);
  const itemType = asTrimmedString(payload?.itemType);
  const detail = asTrimmedString(payload?.detail);
  const candidates: unknown[] = [
    item?.command,
    itemInput?.command,
    itemResult?.command,
    data?.command,
    itemType === "command_execution" && detail ? stripTrailingExitCode(detail).output : null,
  ];

  for (const candidate of candidates) {
    const command = normalizeCommandValue(candidate);
    if (!command) {
      continue;
    }
    return {
      command,
      rawCommand: toRawToolCommand(candidate, command),
    };
  }

  return {
    command: null,
    rawCommand: null,
  };
}

function extractToolTitle(payload: Record<string, unknown> | null): string | null {
  return asTrimmedString(payload?.title);
}

function extractToolCallId(payload: Record<string, unknown> | null): string | null {
  const collabItem = parseCodexCollabToolItem(payload);
  if (collabItem) return collabItem.id;
  const subagentActivity = parseCodexSubagentActivityItem(payload);
  if (subagentActivity) return subagentActivity.id;
  const data = asRecord(payload?.data);
  return asTrimmedString(data?.toolCallId);
}

function normalizeInlinePreview(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function truncateInlinePreview(value: string, maxLength = 84): string {
  if (value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
}

function normalizePreviewForComparison(value: string | null | undefined): string | null {
  const normalized = asTrimmedString(value);
  if (!normalized) {
    return null;
  }
  return normalizeCompactToolLabel(normalizeInlinePreview(normalized)).toLowerCase();
}

function summarizeToolTextOutput(value: string): string | null {
  const lines: Array<string> = [];
  for (const rawLine of value.split(/\r?\n/u)) {
    const line = normalizeInlinePreview(rawLine);
    if (line.length > 0) {
      lines.push(line);
    }
  }
  const firstLine = lines.find((line) => line !== "```");
  if (firstLine) {
    return truncateInlinePreview(firstLine);
  }
  if (lines.length > 1) {
    return `${lines.length.toLocaleString()} lines`;
  }
  return null;
}

function summarizeToolRawOutput(payload: Record<string, unknown> | null): string | null {
  const data = asRecord(payload?.data);
  const rawOutput = asRecord(data?.rawOutput);
  if (!rawOutput) {
    return null;
  }

  const totalFiles = asNumber(rawOutput.totalFiles);
  if (totalFiles !== null) {
    const suffix = rawOutput.truncated === true ? "+" : "";
    return `${totalFiles.toLocaleString()} file${totalFiles === 1 ? "" : "s"}${suffix}`;
  }

  const content = asTrimmedString(rawOutput.content);
  if (content) {
    return summarizeToolTextOutput(content);
  }

  const stdout = asTrimmedString(rawOutput.stdout);
  if (stdout) {
    return summarizeToolTextOutput(stdout);
  }

  return null;
}

function isCommandToolDetail(payload: Record<string, unknown> | null, heading: string): boolean {
  const data = asRecord(payload?.data);
  const kind = asTrimmedString(data?.kind)?.toLowerCase();
  const title = asTrimmedString(payload?.title ?? heading)?.toLowerCase();
  return (
    extractWorkLogItemType(payload) === "command_execution" ||
    kind === "execute" ||
    title === "terminal" ||
    title === "ran command"
  );
}

function extractToolDetail(
  payload: Record<string, unknown> | null,
  heading: string,
): string | null {
  const rawDetail = asTrimmedString(payload?.detail);
  const detail = rawDetail ? stripTrailingExitCode(rawDetail).output : null;
  const normalizedHeading = normalizePreviewForComparison(heading);
  const normalizedDetail = normalizePreviewForComparison(detail);

  if (detail && normalizedHeading !== normalizedDetail) {
    return detail;
  }

  if (isCommandToolDetail(payload, heading)) {
    return null;
  }

  const rawOutputSummary = summarizeToolRawOutput(payload);
  if (rawOutputSummary) {
    const normalizedRawOutputSummary = normalizePreviewForComparison(rawOutputSummary);
    if (normalizedRawOutputSummary !== normalizedHeading) {
      return rawOutputSummary;
    }
  }

  return null;
}

function stripTrailingExitCode(value: string): {
  output: string | null;
  exitCode?: number | undefined;
} {
  const trimmed = value.trim();
  const match = /^(?<output>[\s\S]*?)(?:\s*<exited with exit code (?<code>\d+)>)\s*$/i.exec(
    trimmed,
  );
  if (!match?.groups) {
    return {
      output: trimmed.length > 0 ? trimmed : null,
    };
  }
  const exitCode = Number.parseInt(match.groups.code ?? "", 10);
  const normalizedOutput = match.groups.output?.trim() ?? "";
  return {
    output: normalizedOutput.length > 0 ? normalizedOutput : null,
    ...(Number.isInteger(exitCode) ? { exitCode } : {}),
  };
}

function extractWorkLogItemType(
  payload: Record<string, unknown> | null,
): WorkLogEntry["itemType"] | undefined {
  if (typeof payload?.itemType === "string" && isToolLifecycleItemType(payload.itemType)) {
    return payload.itemType;
  }
  return undefined;
}

function extractWorkLogRequestKind(
  payload: Record<string, unknown> | null,
): WorkLogEntry["requestKind"] | undefined {
  if (
    payload?.requestKind === "command" ||
    payload?.requestKind === "file-read" ||
    payload?.requestKind === "file-change"
  ) {
    return payload.requestKind;
  }
  return requestKindFromRequestType(payload?.requestType) ?? undefined;
}

function pushChangedFile(target: string[], seen: Set<string>, value: unknown) {
  const normalized = asTrimmedString(value);
  if (!normalized || seen.has(normalized)) {
    return;
  }
  seen.add(normalized);
  target.push(normalized);
}

function collectChangedFiles(value: unknown, target: string[], seen: Set<string>, depth: number) {
  if (depth > 4 || target.length >= 12) {
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      collectChangedFiles(entry, target, seen, depth + 1);
      if (target.length >= 12) {
        return;
      }
    }
    return;
  }

  const record = asRecord(value);
  if (!record) {
    return;
  }

  pushChangedFile(target, seen, record.path);
  pushChangedFile(target, seen, record.filePath);
  pushChangedFile(target, seen, record.relativePath);
  pushChangedFile(target, seen, record.filename);
  pushChangedFile(target, seen, record.newPath);
  pushChangedFile(target, seen, record.oldPath);

  for (const nestedKey of [
    "item",
    "result",
    "input",
    "data",
    "changes",
    "files",
    "edits",
    "patch",
    "patches",
    "operations",
  ]) {
    if (!(nestedKey in record)) {
      continue;
    }
    collectChangedFiles(record[nestedKey], target, seen, depth + 1);
    if (target.length >= 12) {
      return;
    }
  }
}

function extractChangedFiles(payload: Record<string, unknown> | null): string[] {
  const changedFiles: string[] = [];
  const seen = new Set<string>();
  collectChangedFiles(asRecord(payload?.data), changedFiles, seen, 0);
  return changedFiles;
}

/**
 * The Delete Design Operations this thread has performed. Delete runs no turn,
 * so each report stands on its own in the timeline at the moment it landed.
 */
export function deriveDesignDeleteReportEntries(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): Array<Extract<TimelineEntry, { kind: "design-delete-report" }>> {
  return activities.flatMap((activity) => {
    const report = readDesignDeleteReport(activity);
    return report === null
      ? []
      : [
          {
            id: activity.id,
            kind: "design-delete-report" as const,
            createdAt: activity.createdAt,
            ...(activity.sequence !== undefined ? { sequence: activity.sequence } : {}),
            report,
          },
        ];
  });
}

export function deriveTimelineEntries(
  messages: ReadonlyArray<ChatMessage>,
  proposedPlans: ReadonlyArray<ProposedPlan>,
  workEntries: ReadonlyArray<WorkLogEntry>,
  options?: {
    readonly messageTurnIds?: ReadonlyMap<string, TurnId>;
    readonly steeredMessageIds?: ReadonlySet<string>;
    readonly operationByMessageId?: ReadonlyMap<string, ThreadOperationIdentity>;
    readonly designDeleteReports?: ReadonlyArray<
      Extract<TimelineEntry, { kind: "design-delete-report" }>
    >;
    readonly workflowCards?: ReadonlyArray<WorkflowToolCard>;
  },
): TimelineEntry[] {
  // Preserve caller message order. Thread Queue turn interleaving puts drained
  // prompts after prior-turn assistants even when those user messages were
  // persisted earlier (smaller createdAt). Sorting every row by createdAt
  // stacks all prompts then all completions and undoes that order.
  const messageRows: Array<Extract<TimelineEntry, { kind: "message" }>> = messages.map(
    (message) => {
      const effectiveTurnId = message.turnId ?? options?.messageTurnIds?.get(message.id) ?? null;
      return {
        id: message.id,
        kind: "message" as const,
        createdAt: message.createdAt,
        ...(message.sequence !== undefined ? { sequence: message.sequence } : {}),
        ...(effectiveTurnId !== null ? { effectiveTurnId } : {}),
        ...(options?.steeredMessageIds?.has(message.id) ? { isSteeredPrompt: true as const } : {}),
        // The message's own operation first: it is durable and present whether
        // or not the send ever waited. The queue entry is the fallback for
        // messages persisted before the identity moved onto the message.
        ...(() => {
          const operation = message.operation ?? options?.operationByMessageId?.get(message.id);
          return operation === undefined ? {} : { operation };
        })(),
        message,
      };
    },
  );
  const extrasByTurnId = new Map<string, TimelineEntry[]>();
  const unassignedExtras: TimelineEntry[] = [];
  const pushExtra = (turnId: string | null | undefined, entry: TimelineEntry) => {
    if (turnId === null || turnId === undefined) {
      unassignedExtras.push(entry);
      return;
    }
    const existing = extrasByTurnId.get(turnId);
    if (existing) {
      existing.push(entry);
    } else {
      extrasByTurnId.set(turnId, [entry]);
    }
  };

  for (const proposedPlan of proposedPlans) {
    pushExtra(proposedPlan.turnId, {
      id: proposedPlan.id,
      kind: "proposed-plan",
      createdAt: proposedPlan.createdAt,
      proposedPlan,
    });
  }
  for (const report of options?.designDeleteReports ?? []) {
    // No turn owns a Delete, so its report is placed by time alone.
    pushExtra(null, report);
  }
  for (const card of options?.workflowCards ?? []) {
    pushExtra(card.turnId, {
      id: card.id,
      kind: "workflow-card",
      createdAt: card.createdAt,
      ...(card.sequence !== undefined ? { sequence: card.sequence } : {}),
      card,
    });
  }
  for (const entry of workEntries) {
    pushExtra(entry.turnId, {
      id: entry.id,
      kind: "work",
      createdAt: entry.createdAt,
      ...(entry.sequence !== undefined ? { sequence: entry.sequence } : {}),
      entry,
    });
  }
  const compareTimelineOrder = (left: TimelineEntry, right: TimelineEntry): number => {
    if (
      left.sequence !== undefined &&
      right.sequence !== undefined &&
      left.sequence !== right.sequence
    ) {
      return left.sequence - right.sequence;
    }
    return left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id);
  };
  for (const entries of extrasByTurnId.values()) {
    entries.sort(compareTimelineOrder);
  }
  unassignedExtras.sort(compareTimelineOrder);

  if (messageRows.length === 0) {
    return [...[...extrasByTurnId.values()].flat(), ...unassignedExtras].toSorted(
      compareTimelineOrder,
    );
  }

  const result: TimelineEntry[] = [];
  const flushedTurnIds = new Set<string>();
  const turnsWithUserMessage = new Set<string>();
  const flushedExtraIds = new Set<string>();
  const flushTurn = (
    turnId: string | null | undefined,
    options?: { readonly untilEntry?: TimelineEntry },
  ) => {
    if (turnId === null || turnId === undefined) {
      return;
    }
    const entries = extrasByTurnId.get(turnId) ?? [];
    const untilEntry = options?.untilEntry;
    for (const entry of entries) {
      if (flushedExtraIds.has(entry.id)) {
        continue;
      }
      if (untilEntry !== undefined && compareTimelineOrder(entry, untilEntry) > 0) {
        continue;
      }
      flushedExtraIds.add(entry.id);
      result.push(entry);
    }
    if (entries.every((entry) => flushedExtraIds.has(entry.id))) {
      flushedTurnIds.add(turnId);
    }
  };
  // Queue-backed ownership is attached to message rows above without mutating
  // persisted messages. When no ownership is available, infer from the next
  // assistant only for work-log flush bookkeeping.
  const effectiveUserTurnId = (fromIndex: number): string | null => {
    const messageRow = messageRows[fromIndex]!;
    const message = messageRow.message;
    if (message.role !== "user") return messageRow.effectiveTurnId ?? null;
    if (messageRow.effectiveTurnId !== undefined) return messageRow.effectiveTurnId;
    for (let index = fromIndex + 1; index < messageRows.length; index += 1) {
      const candidate = messageRows[index]!.message;
      // A later user prompt is a different FIFO turn — do not backfill past it
      // or two null-turnId prompts collapse onto one assistant turn.
      if (candidate.role === "user") return null;
      if (
        candidate.role === "assistant" &&
        candidate.turnId !== null &&
        candidate.turnId !== undefined
      ) {
        return candidate.turnId;
      }
    }
    return null;
  };
  const turnHasLaterUserMessage = (
    fromIndex: number,
    turnId: string | null | undefined,
  ): boolean => {
    if (turnId === null || turnId === undefined) return false;
    for (let index = fromIndex + 1; index < messageRows.length; index += 1) {
      const row = messageRows[index]!;
      if (row.message.role === "user" && effectiveUserTurnId(index) === turnId) {
        return true;
      }
    }
    return false;
  };

  for (let index = 0; index < messageRows.length; index += 1) {
    const messageRow = messageRows[index]!;
    const nextMessageRow = messageRows[index + 1];
    if (messageRow.message.role === "user") {
      const upcomingTurnId = effectiveUserTurnId(index);
      // Finish leftover work from earlier FIFO turns before this prompt so an
      // interrupted Lint's late tools do not land under hey / Query.
      for (const priorTurnId of turnsWithUserMessage) {
        if (priorTurnId !== upcomingTurnId) {
          flushTurn(priorTurnId);
        }
      }
      if (
        upcomingTurnId !== null &&
        turnsWithUserMessage.has(upcomingTurnId) &&
        messageRow.sequence !== undefined
      ) {
        flushTurn(upcomingTurnId, { untilEntry: messageRow });
      }
    }
    if (messageRow.message.role === "assistant") {
      const turnId = messageRow.message.turnId;
      // Only flush before the assistant when the turn's user/action prompt is
      // already on the timeline — and never before a later same-turn user
      // (Steer / multi-prompt). Otherwise Work Log can precede prompts.
      // Flush only extras at-or-before this assistant so later tools stay after
      // interim commentary (tool → message → tool → message).
      if (turnId !== null && turnId !== undefined && turnsWithUserMessage.has(turnId)) {
        if (messageRow.sequence !== undefined || !turnHasLaterUserMessage(index, turnId)) {
          flushTurn(turnId, { untilEntry: messageRow });
        }
      }
    }
    result.push(messageRow);
    if (messageRow.message.role === "user") {
      const turnId = effectiveUserTurnId(index);
      if (turnId !== null) {
        turnsWithUserMessage.add(turnId);
      }
      const nextIsSameTurnAssistant =
        nextMessageRow?.message.role === "assistant" &&
        nextMessageRow.message.turnId !== null &&
        nextMessageRow.message.turnId !== undefined &&
        nextMessageRow.message.turnId === turnId;
      // Also wait when the next row is another user for the same turn (Steer).
      const nextIsSameTurnUser =
        nextMessageRow?.message.role === "user" &&
        effectiveUserTurnId(index + 1) !== null &&
        effectiveUserTurnId(index + 1) === turnId;
      if (
        !nextIsSameTurnAssistant &&
        !nextIsSameTurnUser &&
        !turnHasLaterUserMessage(index, turnId)
      ) {
        flushTurn(turnId);
      }
    }
  }

  for (const turnId of extrasByTurnId.keys()) {
    if (flushedTurnIds.has(turnId)) continue;
    flushTurn(turnId);
  }

  // Unassigned extras keep chronological placement relative to the preserved
  // message stream without reordering messages against each other — and never
  // above the first prompt on an empty thread.
  if (unassignedExtras.length === 0) {
    return result;
  }
  const merged: TimelineEntry[] = [];
  let extraIndex = 0;
  let sawFirstMessage = false;
  for (const entry of result) {
    if (entry.kind === "message") {
      if (!sawFirstMessage) {
        merged.push(entry);
        sawFirstMessage = true;
        while (
          extraIndex < unassignedExtras.length &&
          compareTimelineOrder(unassignedExtras[extraIndex]!, entry) <= 0
        ) {
          merged.push(unassignedExtras[extraIndex]!);
          extraIndex += 1;
        }
        continue;
      }
      while (
        extraIndex < unassignedExtras.length &&
        compareTimelineOrder(unassignedExtras[extraIndex]!, entry) <= 0
      ) {
        merged.push(unassignedExtras[extraIndex]!);
        extraIndex += 1;
      }
    }
    merged.push(entry);
  }
  while (extraIndex < unassignedExtras.length) {
    merged.push(unassignedExtras[extraIndex]!);
    extraIndex += 1;
  }
  return merged;
}

export function inferCheckpointTurnCountByTurnId(
  summaries: ReadonlyArray<TurnDiffSummary>,
): Record<TurnId, number> {
  const sorted = [...summaries].toSorted((a, b) => a.completedAt.localeCompare(b.completedAt));
  const result: Record<TurnId, number> = {};
  for (let index = 0; index < sorted.length; index += 1) {
    const summary = sorted[index];
    if (!summary) continue;
    result[summary.turnId] = index + 1;
  }
  return result;
}

/**
 * Whether the thread is doing work the user can stop.
 *
 * This is the composer's stop control, and it has to cover every state the app
 * labels Working — `connecting` included, which is a session the provider has
 * not acknowledged yet. A thread that reads as Working while offering no way to
 * stop it is the bug this exists to prevent.
 */
export function isThreadWorking(phase: SessionPhase): boolean {
  return phase === "running" || phase === "connecting";
}

export function derivePhase(
  session: ThreadSession | null,
  turn?: { readonly origin: "dispatched" | "provider-initiated"; readonly state: string } | null,
): SessionPhase {
  if (deriveThreadExecutionPhase(turn) === "provider-running") return "running";
  if (
    !session ||
    session.status === "stopped" ||
    session.status === "interrupted" ||
    session.status === "error"
  ) {
    return "disconnected";
  }
  if (session.status === "starting") return "connecting";
  if (session.status === "running") return "running";
  return "ready";
}
